import type { Agent } from '@cotera/sdk/core';
import { llm } from '@cotera/api';
import type { WorkflowNodeViewData } from './types';
import { asRecord, readString } from '../utils';

export type AgentNodeLiveMeta = {
  name: string;
  model: string;
  description: string | null;
};

export function agentIdFromNode(
  node: WorkflowNodeViewData
): string | undefined {
  return readString(asRecord(node.data), 'agentId');
}

export function modelLabel(modelId: string): string {
  const model = (llm.MODELS as Record<string, llm.Model | undefined>)[modelId];
  if (model === undefined) {
    return modelId;
  }
  return model.displayLabel ?? model.name;
}

/** Merges live view-model, event watchable, and agents-list fallback for display. */
export function resolveAgentDisplayMeta(
  agent: Agent | null | undefined,
  config: {
    agentKey: string | null;
    model: string | null;
    description: string | null;
  },
  listMeta: AgentNodeLiveMeta | undefined
): AgentNodeLiveMeta | undefined {
  if (agent !== null && agent !== undefined) {
    return {
      name: agent.title() ?? agent.agentKey,
      model: agent.model,
      description: config.description ?? listMeta?.description ?? null,
    };
  }
  if (config.agentKey !== null && config.model !== null) {
    return {
      name: config.agentKey,
      model: config.model,
      description: config.description ?? listMeta?.description ?? null,
    };
  }
  return listMeta;
}

export function agentTitle(
  node: WorkflowNodeViewData,
  agent: Agent | null | undefined,
  meta?: AgentNodeLiveMeta | null
): string {
  if (agent !== null && agent !== undefined) {
    return agent.title() ?? agent.agentKey;
  }
  if (meta?.name !== undefined && meta.name !== '') {
    return meta.name;
  }
  // Persisted on the node when the agent was picked in the editor; prefer it
  // over the truncated-id fallback while live agent meta is still loading
  // (and on surfaces that never hydrate the agent, e.g. the run graph).
  const savedName = readString(asRecord(node.data), 'name');
  if (savedName !== undefined && savedName !== '') {
    return savedName;
  }
  const agentId = agentIdFromNode(node);
  return agentId !== undefined ? `Agent ${agentId.slice(0, 8)}…` : 'Agent';
}

export function agentDescription(
  node: WorkflowNodeViewData,
  agent: Agent | null | undefined,
  meta?: AgentNodeLiveMeta | null
): string {
  const model =
    agent?.model ?? meta?.model ?? readString(asRecord(node.data), 'model');
  if (model !== undefined && model !== '') {
    return modelLabel(model);
  }
  return 'AI agent';
}

export function agentBodyDescription(
  node: WorkflowNodeViewData,
  agent: Agent | null | undefined,
  meta?: AgentNodeLiveMeta | null
): string {
  const description =
    meta?.description ?? readString(asRecord(node.data), 'description');
  const resolved =
    agent !== null && agent !== undefined
      ? true
      : (meta?.model ?? readString(asRecord(node.data), 'model')) !== undefined;
  if (description !== undefined && description !== '') {
    return description;
  }
  if (resolved) {
    return 'No description set for this agent.';
  }
  return 'Runs the configured agent with workflow input.';
}
